import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, switchMap, take } from 'rxjs';
import { idenfyNameValidator } from 'src/app/core/helpers/custom-name-validator.helper';
import { forbidUppercase } from '../../Helper/custom-form-validators';
import { AccountService } from '../../services/account.service';
import { CommonService } from '../../services/common.service';
import { NotificationService } from '../../services/notification.service';
import { WebStorageService } from '../../services/web-storage.service';
import { EmailPreference, IApiResponse, UserPreferencesPayload } from '../../utils/common.interface';
import { AppkitService } from '../../services/appkit.service';
import { UserStateService } from '../../services/user-state.service';
import { LoggerService } from '../../services/logger.service';
import { UserRegisterTypeEnum } from '../../utils/user-register-type-enum';
import { AuthService as AppAuthService } from '../../services/auth.service';
import { AuthReturnUrlService } from '../../services/auth-return-url.service';
import { NgIf, NgClass, NgFor } from '@angular/common';

@Component({
    selector: 'app-mailverfication',
    templateUrl: './mailverfication.component.html',
    styleUrls: ['./mailverfication.component.css'],
    imports: [NgIf, FormsModule, ReactiveFormsModule, NgClass, NgFor]
})
export class MailverficationComponent implements OnInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    private resendCooldownTimer: ReturnType<typeof setTimeout> | null = null;
    private isConfirmingEmail = false;
    @Input() showMailVerificationMenu = true;
    submitted = false;
    emailForm!: FormGroup;
    emailSettingsForm!: FormGroup;
    showStepOne = false;
    showStepTwo = false;
    showStepThree = false;
    isEditing = false;
    @Output() closeModal = new EventEmitter();
    @Output() openModal = new EventEmitter();
    user!: { [key: string]: any };
    isDisabled = false;
    @Input() isCancelOption = false;
    queryParams: any;
    emailPreferences: any = [];
    apiProcessing = false;
    isPendingSignature = false;
    is_Triggered: boolean = false;
    is_Checked: { [key: string]: boolean } = {
        'agreement_checkbox': false
    }
    signature!: string;
    docUrl: any;

    /**
     * constructor
     */
    constructor(
        private fb: FormBuilder,
        private accountService: AccountService,
        private toastr: ToastrService,
        private webStorageService: WebStorageService,
        private route: ActivatedRoute,
        private router: Router,
        private notificationService: NotificationService,
        public commonService: CommonService,
        private appkitService: AppkitService,
        private userState: UserStateService,
        private logger: LoggerService,
        private appAuthService: AppAuthService,
        private authReturnUrl: AuthReturnUrlService
    ) {
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['showMailVerificationMenu'] && changes['showMailVerificationMenu'].currentValue) {
            this.route.queryParams
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe(params => {
                    const id = params['id'];
                    const token = params['token'];
                    if (id && token) {
                        const latestUser = this.userState.user || this.user;
                        if (latestUser?.['email_verified'] === true) {
                            this.openModal.emit();
                            this.showStepThree = true;
                        } else {
                            this.confirmEmail(id, token);
                        }
                    }
                });
        }

        if (changes['showMailVerificationMenu'] && changes['showMailVerificationMenu'].currentValue === false) {
            this.refreshUserState();
            this.loadEmailPreferencesIfEligible();
        }
    }
    ngOnInit() {
        this.route.queryParams
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(queryParams => {
                this.queryParams = queryParams;
                this.syncFormWithUser();

                const id = queryParams['id'];
                const token = queryParams['token'];
                if (id && token) {
                    const latestUser = this.userState.user || this.user;
                    if (latestUser?.['email_verified'] === true) {
                        this.openModal.emit();
                        this.showStepThree = true;
                    } else {
                        this.confirmEmail(id, token);
                    }
                }
            });

        this.refreshUserState();
        this.emailForm = this.fb.group({
            first_name: ['', [Validators.required, idenfyNameValidator()]],
            last_name: ['', [Validators.required, idenfyNameValidator()]],
            email: ['', [Validators.required, Validators.email, forbidUppercase()]],
            wallet_address: [''],
        })
        this.syncFormWithUser();
        this.loadEmailPreferencesIfEligible();
        this.userState.user$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user) => {
                if (!user || this.isConfirmingEmail) {
                    return;
                }

                this.user = user;
                this.syncFormWithUser(user);
                this.syncVerificationStepState(user);
            });
        // console.log('mail verification component');
        // this.commonService.getDocument();
        this.commonService.getDocument().subscribe({
            next: (docUrls) => {
                this.docUrl = docUrls;
            },
            error: (err) => {
                this.logger.error('Failed to fetch mail verification documents', err);
            }
        });
    }
    get f() { return this.emailForm?.controls as { [key: string]: AbstractControl }; }

    public isEmailVerificationPending(): boolean {
        return this.user?.['email_verified'] === false || this.userState.user?.email_verified === false;
    }

    public shouldShowProfileDetailsStep(): boolean {
        return this.showStepOne || this.isEditing || !this.hasCompletedVerificationProfile();
    }

    public shouldShowPendingVerificationStep(): boolean {
        if (this.showStepThree || this.shouldShowProfileDetailsStep()) {
            return false;
        }

        return this.showStepTwo || this.isEmailVerificationPending();
    }

    private refreshUserState(): void {
        if (this.userState.user) {
            this.user = this.userState.user;
            return;
        }

        const storedUser = this.webStorageService.getLocalStorage('user');
        this.user = storedUser != null ? JSON.parse(storedUser || 'undefined') : this.user;
    }

    private syncFormWithUser(user: { [key: string]: any } = this.user): void {
        if (!this.emailForm || !user) {
            return;
        }

        this.emailForm.patchValue({
            first_name: user['first_name'] ?? '',
            last_name: user['last_name'] ?? '',
            email: user['email'] ?? '',
            wallet_address: user['wallet_address'] ?? '',
        }, { emitEvent: false });
    }

    private syncVerificationStepState(user: { [key: string]: any } = this.user): void {
        if (!user || this.isEditing || this.showStepThree) {
            return;
        }

        const hasCompletedProfile = this.hasCompletedVerificationProfile(user);
        this.showStepOne = !hasCompletedProfile;
        this.showStepTwo = hasCompletedProfile && user['email_verified'] !== true;
    }

    private hasCompletedVerificationProfile(user: { [key: string]: any } = this.user): boolean {
        if (!user) {
            return false;
        }

        if (user['is_valid'] === true) {
            return true;
        }

        return this.hasRequiredVerificationDetail(user['first_name'])
            && this.hasRequiredVerificationDetail(user['last_name'])
            && this.hasRequiredVerificationDetail(user['email']);
    }

    private hasRequiredVerificationDetail(value: unknown): boolean {
        return String(value ?? '').trim().length > 0;
    }

    private loadEmailPreferencesIfEligible(): void {
        this.refreshUserState();

        if (!this.user?.['_id'] || this.user?.['email_verified'] !== true) {
            return;
        }

        this.getEmailPreferenceByUserId();
    }

    onSubmit() {
        this.emailForm.patchValue({
            wallet_address: this.user?.['wallet_address'],
        });
        this.submitted = true;

        if (this.emailForm.invalid || !this.signature) {
            this.submitted = false;
            this.toastr.error(
                this.emailForm.invalid
                    ? 'Please fill all the required fields.'
                    : 'Please agree to the Privacy Policy and Terms of Service to proceed.'
            );
            this.emailForm.markAllAsTouched();
            return;
        }
        const payload = {
            ...this.emailForm.value,
            user_register_type: this.getUserRegisterType(),
            signature: this.signature
        }

        this.accountService.updateProfile(payload).subscribe({
            next: (response: IApiResponse) => {
                const updatedUser = response?.data || this.user;

                if (updatedUser) {
                    this.appAuthService.syncAuthenticatedUserState(updatedUser, {
                        regulated: this.userState.regulated,
                        fallbackAccount: this.userState.account
                    });
                    this.user = updatedUser;
                    this.syncFormWithUser(updatedUser);
                }

                this.showStepTwo = true;
                this.showStepOne = this.isEditing = false;
                this.emailForm.reset();
                this.submitted = false;
            },
            error: (error: HttpErrorResponse) => {
                this.toastr.error(error?.error?.message || "Something went wrong, try again later.");
                this.submitted = false;
            }
        })
    }

    private getUserRegisterType(): UserRegisterTypeEnum {
        const storedRegisterType = this.webStorageService.getLocalStorage('user_register_type');
        const parsedRegisterType = typeof storedRegisterType === 'string'
            ? Number(storedRegisterType)
            : Number(storedRegisterType ?? NaN);
        if (Number.isFinite(parsedRegisterType) && parsedRegisterType > 0) {
            return parsedRegisterType as UserRegisterTypeEnum;
        }
        return UserRegisterTypeEnum.NEUTRAL;
    }

    /**
   * resend verification email
   */
    resendVerificationEmail() {
        this.isDisabled = true;
        this.accountService.resendVerificationEmail(this.user['email']).subscribe({
            next: (response: IApiResponse) => {
                this.toastr.success(response.message);
                this.resendCooldownTimer = setTimeout(() => {
                    this.isDisabled = false;
                    this.resendCooldownTimer = null;
                }, 10000);
            },
            error: (error: HttpErrorResponse) => {
                this.toastr.error(error?.error?.message || "Something went wrong, try again later.");
                this.isDisabled = false;
            }
        })
    }

    /**
     * on edit
     */
    onEdit() {
        this.showStepOne = this.isEditing = true;
        this.showStepTwo = false;
        this.refreshUserState();
        this.syncFormWithUser();

    }

    /**
     * close step one
     */
    closeStepOne() {
        this.showStepOne = this.isEditing = false;
        this.cleanupBackdrop();
        this.closeModal.emit();
        this.emailForm.reset()
    }






    /**
   * confirm email
   * @param {string}id
   * @param {string}token
   */
    isLoggedIn: boolean = false
    private confirmEmail(id: string, token: string) {
        if (this.isConfirmingEmail) {
            return;
        }
        this.isConfirmingEmail = true;

        this.accountService.confirmEmail({ id: id, token: token }).subscribe({
            next: (response: IApiResponse) => {
                const existingUser = this.userState.user;
                this.isLoggedIn = !!existingUser;

                this.appAuthService.getCurrentUser(true)
                    .pipe(
                        catchError(() => of(null)),
                        switchMap((sessionUser) => {
                            if (sessionUser) {
                                return of(sessionUser);
                            }

                            const fallbackUser = response?.data || existingUser || this.user;
                            return of(fallbackUser ?? null);
                        })
                    )
                    .subscribe((verifiedUser) => {
                        if (verifiedUser) {
                            this.appAuthService.syncAuthenticatedUserState(verifiedUser, {
                                regulated: this.resolveVerifiedUserRegulatedState(),
                                fallbackAccount: this.userState.account
                            });
                            this.user = {
                                ...verifiedUser,
                                email_verified: true
                            };
                            this.syncFormWithUser(this.user);
                            this.loadEmailPreferencesIfEligible();
                        }

                        this.isConfirmingEmail = false;
                        this.openModal.emit();
                        this.showStepThree = true;
                    });

            },
            error: (error: HttpErrorResponse) => {
                this.isConfirmingEmail = false;
                this.toastr.error(error?.error?.message || "Something went wrong, try again later.");
            }
        })
    }

    private resolveVerifiedUserRegulatedState(): boolean {
        return this.userState.regulated;
    }

    createFgForEmailSettings(emailPreferences: any[]) {
    const formGroup: any = {};
    emailPreferences.forEach((group: any) => {

        group.items.forEach((item: EmailPreference) => {            
            formGroup[item.key] = [item.value];
        });
    });
    this.emailSettingsForm = this.fb.group(formGroup);
}

    getEmailPreferenceByUserId() {
        if (!this.user?.['_id'] || this.user?.['email_verified'] !== true) { return; }
        this.notificationService.getEmailPreferencesByUserId().subscribe({
            next: (response: IApiResponse) => {
                this.emailPreferences = response?.data;
                this.createFgForEmailSettings(response?.data);
            },
            error: (error: HttpErrorResponse) => {
                this.toastr.error(error?.error?.message || "Failed to get email preferences. Please try again.");
            }
        })
    }

    saveSettings() {
        this.apiProcessing = true;
        const payload: UserPreferencesPayload = this.generatePreferences();
        this.notificationService.updateEmailPreferences(payload).subscribe({
            next: (response: IApiResponse) => {
                this.emailPreferences = response?.data?.preferences;
                this.toastr.success(response?.message || "Email preferences updated successfully.");
                this.getEmailPreferenceByUserId();
                this.apiProcessing = false;
                this.cleanupBackdrop();
                this.closeModal.emit();
            },
            error: (error: HttpErrorResponse) => {
                this.apiProcessing = false;
                this.toastr.error(error?.error?.message || "Failed to update email preferences. Please try again.");
            }

        })
    }
  generatePreferences() {
    const emailPreferences = this.emailSettingsForm.getRawValue();

    const preferences: any[] = [];
    this.emailPreferences.forEach((group: any) => {
        group.items.forEach((item: any) => {
            preferences.push({
                key: item.key,
                name: item.name,
                description: item.description,
                value: emailPreferences[item.key]
            });
        });
    });

    return {
        user_id: this.user['_id'],
        preferences
    };
}

  selectAll() {
    const allSelected = this.checkSelectAll();
    this.emailPreferences.forEach((group: any) => {
        group.items.forEach((item: EmailPreference) => {
            item.value = !allSelected;
        });
    });
    this.createFgForEmailSettings(this.emailPreferences);
}


   checkSelectAll(): boolean {
    const formValues = Object.values(this.emailSettingsForm?.value);
    return formValues.length > 0 && formValues.every((val) => val === true);
}

    /**
     * Handles the signature retrieval process when the agreement checkbox is clicked.
     * Prevents the default checkbox behavior until the signature is successfully retrieved.
     *
     * @param {Event} event - The click event triggered by the agreement checkbox.
     */
    triggerSignatureRetrieval(event: Event): void {
        event.preventDefault();
        if (!this.is_Triggered) {
            this.isPendingSignature = true;
            this.is_Triggered = true;
           const message= 'By approving this signature, you agree to the Privacy Policy and User Agreement.';
           this.appkitService.signMessage(message).then(signature => {
                this.logger.debug('Privacy policy signature captured');
                this.signature = signature;
                this.isPendingSignature = false;
                this.is_Checked['agreement_checkbox'] = true;
            }).catch(err => {
                this.is_Checked['agreement_checkbox'] = false;
                this.isPendingSignature = false;
                this.is_Triggered = false;
                this.toastr.error('Please select the checkbox to sign the privacy policy and user aggrement to proceed.')
            });
        }
    }

    /**
     * Closes the modal and cleans up any modal-related classes and elements from the DOM.
     */
    closeSettingsModal() {
        this.cleanupBackdrop();
        this.closeModal.emit();
    }

    /**
     * Removes modal-related classes from the body and removes any existing modal backdrop elements from the DOM to ensure a clean state when closing modals.
     */
    private cleanupBackdrop() {
        document.body.classList.remove('modal-open');
        document.querySelectorAll('.modal-backdrop')
            .forEach(el => el.remove());
    }

    /**
     * Closes the modal without reloading (used when email is pending verification).
     */
    closeModalOnly() {
        if (this.isEmailVerificationPending()) {
            return;
        }

        this.cleanupBackdrop();
        this.closeModal.emit();
    }

    /**
     * Closes modal verification
     * Navigates via the Angular router so in-memory auth state (populated by the preceding
     * syncAuthenticatedUserState call in confirmEmail) is preserved across the navigation:
     * to the shared link the user registered from (handed over from the tab that sent the
     * email), otherwise the dashboard.
     */
    closeModalVerification() {
        this.cleanupBackdrop();
        this.closeModal.emit();
        this.router.navigateByUrl(this.authReturnUrl.consumeOpenedLink() ?? '/dashboard');
    }

    ngOnDestroy(): void {
        if (this.resendCooldownTimer !== null) {
            clearTimeout(this.resendCooldownTimer);
        }
        this.cleanupBackdrop();
    }
}
